import { useState, type CSSProperties, type ReactNode } from 'react'
import { getInitials } from '@/components/ui/avatar-utils'
import { LogoMark } from '@/components/ui/Logo'
import { cn } from '@/components/ui/cn'

export interface CoverImageProps {
  src?: string | null
  alt?: string
  /** Community or course name, used for the initials on the fallback. */
  name?: string
  /** CSS aspect ratio: "16/9" (default), "1.9/1", or "auto" to fill the parent. */
  aspect?: '16/9' | '1.9/1' | 'auto'
  /** Show initials on the fallback (default true). */
  initials?: boolean
  rounded?: boolean
  className?: string
  imgClassName?: string
  children?: ReactNode
}

/**
 * Cover image with a light default fallback (REDESIGN section 9): a very light gray panel with the initials,
 * or the MG mark when there is no name to show. Never dark navy, no gradient, no pattern.
 */
export function CoverImage({
  src,
  alt = '',
  name,
  aspect = '16/9',
  initials = true,
  rounded = false,
  className,
  imgClassName,
  children,
}: CoverImageProps) {
  const [failed, setFailed] = useState<string | null>(null)
  const showImage = !!src && failed !== src
  return (
    <div
      className={cn('relative w-full overflow-hidden bg-surface-2', rounded && 'rounded-card', aspect === 'auto' && 'h-full', className)}
      style={{ containerType: 'inline-size', ...(aspect === 'auto' ? {} : { aspectRatio: aspect.replace('/', ' / ') }) } as CSSProperties}
    >
      {showImage ? (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          draggable={false}
          onError={() => setFailed(src)}
          className={cn('absolute inset-0 size-full object-cover', imgClassName)}
        />
      ) : (
        <div
          className="absolute inset-0 bg-surface-2 text-subtle"
          data-cover-fallback=""
          role={name ? 'img' : undefined}
          aria-label={name || undefined}
        >
          {initials && name ? (
            <span
              aria-hidden="true"
              className="absolute inset-0 flex items-center justify-center font-extrabold tracking-tight"
              style={{ fontSize: 'clamp(24px, 11cqw, 72px)' }}
            >
              {getInitials(name)}
            </span>
          ) : (
            <span aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
              <LogoMark className="h-[clamp(24px,18cqw,96px)] w-auto opacity-90" />
            </span>
          )}
        </div>
      )}
      {children}
    </div>
  )
}
